const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

async function parseJsonSafe(response) {
  try {
    return await response.json();
  } catch {
    return {};
  }
}

async function apiRequest(
  endpoint,
  { accessToken, method = "GET", body, isFormData = false } = {},
) {
  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };

  if (!isFormData) {
    headers["Content-Type"] = "application/json";
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method,
    headers,
    body: isFormData ? body : body ? JSON.stringify(body) : undefined,
  });

  const data = await parseJsonSafe(response);

  if (!response.ok) {
    throw new Error(
      data?.detail ||
        data?.error ||
        data?.message ||
        "Something went wrong while communicating with the server.",
    );
  }

  return data;
}

/* -------------------- THREADS -------------------- */

export async function fetchCommunicationThreads({ accessToken }) {
  return apiRequest("/api/communication/threads/", {
    accessToken,
    method: "GET",
  });
}

export async function fetchDossierCommunicationThreads({
  accessToken,
  dossierId,
}) {
  return apiRequest(`/api/dossiers/${dossierId}/communication/threads/`, {
    accessToken,
    method: "GET",
  });
}

export async function fetchCommunicationThreadDetail({
  accessToken,
  threadId,
}) {
  return apiRequest(`/api/communication/threads/${threadId}/`, {
    accessToken,
    method: "GET",
  });
}

export async function createCommunicationThread({ accessToken, payload }) {
  return apiRequest("/api/communication/threads/create/", {
    accessToken,
    method: "POST",
    body: payload,
  });
}

export async function updateCommunicationThread({
  accessToken,
  threadId,
  payload,
}) {
  return apiRequest(`/api/communication/threads/${threadId}/update/`, {
    accessToken,
    method: "PATCH",
    body: payload,
  });
}

export async function archiveCommunicationThread({ accessToken, threadId }) {
  return apiRequest(`/api/communication/threads/${threadId}/archive/`, {
    accessToken,
    method: "PATCH",
  });
}

export async function markThreadAsRead({ accessToken, threadId }) {
  return apiRequest(`/api/communication/threads/${threadId}/read/`, {
    accessToken,
    method: "POST",
  });
}

/* -------------------- MESSAGES -------------------- */

export async function fetchThreadMessages({ accessToken, threadId }) {
  return apiRequest(`/api/communication/threads/${threadId}/messages/`, {
    accessToken,
    method: "GET",
  });
}

export async function createThreadMessage({
  accessToken,
  threadId,
  body,
  messageType = "COMMENT",
  visibility = "ALL",
  payload,
  replyTo,
  relatedObjectType,
  relatedObjectId,
}) {
  const requestBody = {
    body,
    message_type: messageType,
    visibility,
  };

  if (payload !== undefined) {
    requestBody.payload = payload;
  }

  if (replyTo !== undefined && replyTo !== null) {
    requestBody.reply_to = replyTo;
  }

  if (relatedObjectType) {
    requestBody.related_object_type = relatedObjectType;
  }

  if (relatedObjectId !== undefined && relatedObjectId !== null) {
    requestBody.related_object_id = relatedObjectId;
  }

  return apiRequest(`/api/communication/threads/${threadId}/messages/create/`, {
    accessToken,
    method: "POST",
    body: requestBody,
  });
}

export async function updateThreadMessage({ accessToken, messageId, payload }) {
  return apiRequest(`/api/communication/messages/${messageId}/update/`, {
    accessToken,
    method: "PATCH",
    body: payload,
  });
}

export async function deleteThreadMessage({
  accessToken,
  threadId,
  messageId,
}) {
  return apiRequest(
    `/api/communication/threads/${threadId}/messages/${messageId}/delete/`,
    {
      accessToken,
      method: "DELETE",
    },
  );
}

/* -------------------- NOTIFICATIONS -------------------- */

export async function fetchCommunicationNotifications({ accessToken }) {
  return apiRequest("/api/communication/notifications/", {
    accessToken,
    method: "GET",
  });
}

export async function fetchCommunicationNotificationsSummary({ accessToken }) {
  return apiRequest("/api/communication/notifications/summary/", {
    accessToken,
    method: "GET",
  });
}

export async function markNotificationAsRead({ accessToken, notificationId }) {
  return apiRequest(
    `/api/communication/notifications/${notificationId}/read/`,
    {
      accessToken,
      method: "PATCH",
    },
  );
}

export async function markAllNotificationsAsRead({ accessToken }) {
  return apiRequest("/api/communication/notifications/read-all/", {
    accessToken,
    method: "PATCH",
  });
}
